import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup } from 'semantic-ui-react';
import { Package, Signature, X } from '@phosphor-icons/react';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { SkupremeModal } from '../../components';
import {
  HoverBorderButton,
  NumberFormField,
  RadioFormField,
  StyledButton,
} from '../../elements';
import {
  CommonUtility,
  InventoryService,
  theme,
  ToastMessage,
  tooltipTexts,
} from '../../utility';
import { MetricImperialToggle } from '../../components/MetricImperialToggle';
import { ConversionUtility } from '../../utility/conversions';

const XIcon = styled(X)`
  margin: 0 5px;
  margin-top: 34px;
  min-width: 17px;
`;

const ItemSchema = yup.object().shape({
  length: yup.number().positive('Length is required').typeError('Length is required'),
  width: yup.number().positive('Width is required').typeError('Width is required'),
  height: yup.number().positive('Height is required').typeError('Height is required'),
  weight: yup.number().positive('Weight is required').typeError('Weight is required'),
  signature: yup.boolean(),
  preference: yup.string(),
});

const decimalMask = {
  allowDecimal: true,
  allowLeadingZeroes: false,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

const UnitLabel = styled.div`
  border-left: none !important;
  &.error {
    border-color: #e0b4b4 !important;
    color: #e7bdbc !important;
    background: #fff6f6 !important;
  }
  font-weight: 400 !important;
  color: ${({ theme }) => theme.colors.grey} !important;
`;

const attributeSchema = {
  height: '',
  length: '',
  width: '',
  weight: '',
};

export function EditAttributePopup({ item, onClose, refresh, open }) {
  const [loading, setLoading] = useState(false);
  const [ImperialForm, setImperialForm] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  useEffect(() => {
    if (!CommonUtility.isValidObject(item)) return;

    let itemToReset = {
      length: item.product.package_dimensions?.length?.value,
      width: item.product.package_dimensions?.width?.value,
      height: item.product.package_dimensions?.height?.value,
      weight: item.product.package_dimensions?.weight?.value,
      signature: item.product.package_dimensions?.signature || false,
      preference: item.product.package_dimensions?.ship_in_mailer
        ? 'mailer'
        : item.product.package_dimensions?.ship_in_box
          ? 'box'
          : item.product.package_dimensions?.single_first_packaging
            ? 'single_first_packaging'
            : item.product.package_dimensions?.strapped_bundle
              ? 'strapped_bundle'
              : 'none',
    };

    const isOriginallyMetric = item.product.package_dimensions?.height?.unit === 'cm';

    if (isOriginallyMetric) {
      const convertedFields = convertFieldsToImperial(itemToReset);
      itemToReset = {
        ...itemToReset,
        ...convertedFields,
      };
    }

    reset(itemToReset);

    setImperialForm(true);
  }, [item]);

  const changeUnits = () => {
    const watchFields = getValues();
    let newItem = null;

    if (ImperialForm) {
      newItem = convertFieldsToMetric(watchFields);
    } else {
      newItem = convertFieldsToImperial(watchFields);
    }

    reset({
      ...watchFields,
      ...newItem,
    });

    setImperialForm(!ImperialForm);
  };

  const convertFieldsToMetric = watchFields => {
    const item = attributeSchema;

    Object.keys(attributeSchema).forEach(key => {
      if (['weight'].includes(key)) {
        item[key] = ConversionUtility.poundToKilo(watchFields[key]);
      } else {
        item[key] = ConversionUtility.inchToCenti(watchFields[key]);
      }
    });

    return item;
  };

  const convertFieldsToImperial = watchFields => {
    const item = attributeSchema;

    Object.keys(attributeSchema).forEach(key => {
      if (['weight'].includes(key)) {
        item[key] = ConversionUtility.kiloToPound(watchFields[key]);
      } else {
        item[key] = ConversionUtility.centiToInch(watchFields[key]);
      }
    });

    return item;
  };

  const onSubmit = async formValues => {
    try {
      setLoading(true);
      const payload = {
        package_dimensions: {
          length: {
            value: formValues.length,
            unit: 'cm',
          },
          width: {
            value: formValues.width,
            unit: 'cm',
          },
          height: {
            value: formValues.height,
            unit: 'cm',
          },
          weight: {
            value: formValues.weight,
            unit: 'kg',
          },
          ship_in_mailer: formValues.preference === 'mailer',
          ship_in_box: formValues.preference === 'box',
          signature: formValues.signature,
          single_first_packaging: formValues.preference === 'single_first_packaging',
          strapped_bundle: formValues.preference === 'strapped_bundle',
        },
      };

      if (ImperialForm) {
        payload.package_dimensions.length.value = ConversionUtility.inchToCenti(
          payload.package_dimensions.length.value,
        );
        payload.package_dimensions.width.value = ConversionUtility.inchToCenti(
          payload.package_dimensions.width.value,
        );
        payload.package_dimensions.height.value = ConversionUtility.inchToCenti(
          payload.package_dimensions.height.value,
        );
        payload.package_dimensions.weight.value = ConversionUtility.poundToKilo(
          payload.package_dimensions.weight.value,
        );
      }

      await InventoryService.editAttribute(item._id, payload);
      ToastMessage.success('Attribute updated successfully');
      onClose();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SkupremeModal
      size="small"
      onClose={onClose}
      open={open}
      title={`Edit ${item?.product?.p_sku}`}
    >
      <MetricImperialToggle
        toggle
        onChange={() => changeUnits()}
        checked={ImperialForm}
        className="mx-3"
        wrapperClassName="mt-2 mb-3"
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <b>Product Size</b>
        <FormGroup widths="equal">
          <NumberFormField
            control={control}
            name="length"
            label="Length"
            error={errors.length}
            hint={errors.length?.message}
            maskOptions={decimalMask}
            placeholder="L"
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.length && 'error'}`}>
                {ImperialForm ? 'in.' : 'cm'}
              </UnitLabel>
            }
          />
          <XIcon size={17} />
          <NumberFormField
            control={control}
            maskOptions={decimalMask}
            name="width"
            label="Width"
            error={errors.width}
            hint={errors.width?.message}
            className="align-unset fluid ui right labeled input"
            placeholder="W"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.width && 'error'}`}>
                {ImperialForm ? 'in.' : 'cm'}
              </UnitLabel>
            }
          />
          <XIcon size={17} />
          <NumberFormField
            maskOptions={decimalMask}
            control={control}
            name="height"
            label="Height"
            error={errors.height}
            hint={errors.height?.message}
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.height && 'error'}`}>
                {ImperialForm ? 'in.' : 'cm'}
              </UnitLabel>
            }
            placeholder="H"
          />
          <NumberFormField
            control={control}
            maskOptions={decimalMask}
            name="weight"
            label="Net Weight"
            error={errors.weight}
            hint={errors.weight?.message}
            fieldClass="ml-4"
            placeholder="Net"
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.weight && 'error'}`}>
                {ImperialForm ? 'lb' : 'kg'}
              </UnitLabel>
            }
          />
        </FormGroup>
        <b>Fulfillment</b>
        <div className="d-flex align-items-center my-3 row">
          <div className="d-flex align-items-center col-4 pl-0">
            <Signature size={17} color={theme.colors.grey} className="mr-1" />
            <span>Require a Signature</span>
          </div>
          <RadioFormField
            control={control}
            name="signature"
            error={errors.signature}
            hint={errors.signature?.message}
            fieldClass="mb-0"
            options={[
              {
                value: true,
                text: 'Yes',
              },
              {
                value: false,
                text: 'No',
              },
            ]}
          />
        </div>
        <div className="d-flex row">
          <div className="d-flex col-3 pl-0 mr-4">
            <Package size={17} color={theme.colors.grey} className="mr-1" />
            <span>Preference</span>
          </div>
          <RadioFormField
            control={control}
            name="preference"
            error={errors.preference}
            hint={errors.preference?.message}
            fieldClass="mb-0 col-8 ml-4"
            className="mr-3 ml-2 mb-1"
            options={[
              {
                value: 'box',
                text: 'Ships in Product Packaging',
                helpText: tooltipTexts.shipInBox,
              },
              {
                value: 'mailer',
                text: 'Mailer',
                helpText: tooltipTexts.mailer,
              },
              {
                value: 'single_first_packaging',
                text: 'Single Package First',
                helpText: tooltipTexts.singleFirstPackaging,
              },
              {
                value: 'strapped_bundle',
                text: 'Strapped Bundle',
                helpText: tooltipTexts.strappedBundle,
              },
              {
                value: 'none',
                text: 'None',
                helpText: tooltipTexts.noSippingPreference,
              },
            ]}
          />
        </div>
        <div className="d-flex justify-content-end mt-4">
          <HoverBorderButton onClick={onClose} disabled={loading} type="button">
            Cancel
          </HoverBorderButton>
          <StyledButton loading={loading} className="ml-1">
            Save Changes
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
